import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getConfig } from "../utils/config";
import { getStoryUrl } from "../utils/storyUrl";
import { getStoryReadyTime } from "../utils/storyReadyTime";
import { trackEvent } from "../utils/analytics";
import { decodeGuidToString, formatStoryCountdown } from "../helpers";
import "./SessionCompleted.scss";

export const SessionCompleted: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("s");
  const sessionEndTime = searchParams.get("d");
  const storyReadyTime = sessionEndTime ? getStoryReadyTime(new Date(sessionEndTime)) : null;
  const [currentTime, setCurrentTime] = useState(() => new Date());

  const canViewStory = storyReadyTime ? currentTime >= storyReadyTime : false;
  const decodedSessionId = sessionId ? decodeGuidToString(sessionId) : null;

  const [storyUrl, setStoryUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!storyReadyTime || canViewStory) {
      return;
    }

    const intervalId = window.setInterval(() => setCurrentTime(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [storyReadyTime, canViewStory]);

  useEffect(() => {
    if (canViewStory) {
      getConfig()
        .then((config) => {
          setStoryUrl(getStoryUrl(config.storyRegionDomain, decodedSessionId));
        })
        .catch(() => {}); // fall back to "story not ready" copy
    }
  }, [canViewStory, decodedSessionId]);

  const storyCountdown = storyReadyTime
    ? formatStoryCountdown(Math.max(0, storyReadyTime.getTime() - currentTime.getTime()))
    : null;

  return (
    <div className="pageContainer">
      <div className="logoContainer">
        <img src={"./img/es-logo.svg"} width="110px" alt="Electric Shuffle" />
      </div>
      <div className="contentCard completeSession">
        <img src="./img/disco-ball.svg" className="discoBall" alt="Disco ball" />
        <div className="sessionCompletedContent">
          <div className="sessionCompletedTitle">Well Played!</div>
          {!storyUrl && (
            <>
              <div className="sessionCompletedText">
                We're putting together the best bits from your game in your own highlights reel. Check back
                when the countdown ends to see it!
                {storyCountdown && (
                  <span className="storyCountdown" aria-live="polite">
                    {storyCountdown}
                  </span>
                )}
              </div>
              <img src="./img/pucks-stars.svg" className="pucksStars" alt="Pucks and stars" />
            </>
          )}
          {storyUrl && (
            <>
              <div className="sessionCompletedText">
                The game is over but your fun doesn't stop there. Check out the game highlights in your Story
                using the link below.
              </div>
              <div className="storyButtons">
                <button
                  className="small"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_story",
                      clarityEvent: "session_complete_story",
                    });
                  }}
                >
                  <a
                    className="viewStoryLink"
                    href={`${storyUrl}?utm_source=rapid_entry&utm_medium=session_complete_story`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Enjoy your story
                  </a>
                </button>
                <button
                  className="small cream"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_book",
                      clarityEvent: "session_complete_book",
                    });
                  }}
                >
                  <a
                    className="orange"
                    href="https://electricshuffle.com/?utm_source=rapid_entry&utm_medium=session_complete"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Next Round
                  </a>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {!storyUrl && (
        <footer className="btn-container">
          <button className="button">
            <a href="https://www.electricshuffle.com/" target="_blank" rel="noopener noreferrer">
              Book Next Round
            </a>
          </button>
        </footer>
      )}
    </div>
  );
};
